import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, Button, Paper, Alert, CircularProgress,
  Fade, Divider, Chip, Stepper, Step, StepLabel, InputAdornment,
  Tabs, Tab, MenuItem, Select, FormControl, InputLabel, Dialog,
  DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText,
  IconButton, Tooltip, Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import {
  Search, Building2, Globe, Rocket, Check, ShieldCheck, Copy, ExternalLink,
  Plus, Trash2, Server, RefreshCw, AlertCircle, Play, X as XIcon,
  Hourglass, AlertTriangle,
} from 'lucide-react';
import { styled } from '@mui/material/styles';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;

const SectionCard = styled(Paper)(({ theme }) => ({
  background: '#141926',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 16,
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const formatSlug = (input) => (input || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/\s+/g, '-')
  .replace(/[^a-z0-9-]/g, '')
  .replace(/-+/g, '-')
  .replace(/^-+|-+$/g, '')
  .substring(0, 30);

const formatBucketSlug = (input) => (input || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]/g, '')
  .substring(0, 40);

const formatCNPJ = (v) => {
  const n = (v || '').replace(/\D/g, '').substring(0, 14);
  if (n.length <= 2) return n;
  if (n.length <= 5) return `${n.slice(0, 2)}.${n.slice(2)}`;
  if (n.length <= 8) return `${n.slice(0, 2)}.${n.slice(2, 5)}.${n.slice(5)}`;
  if (n.length <= 12) return `${n.slice(0, 2)}.${n.slice(2, 5)}.${n.slice(5, 8)}/${n.slice(8)}`;
  return `${n.slice(0, 2)}.${n.slice(2, 5)}.${n.slice(5, 8)}/${n.slice(8, 12)}-${n.slice(12)}`;
};

const formatTelefoneInput = (v) => {
  const n = (v || '').replace(/\D/g, '').substring(0, 11);
  if (n.length <= 2) return n;
  if (n.length <= 6) return `(${n.slice(0, 2)}) ${n.slice(2)}`;
  if (n.length <= 10) return `(${n.slice(0, 2)}) ${n.slice(2, 6)}-${n.slice(6)}`;
  return `(${n.slice(0, 2)}) ${n.slice(2, 7)}-${n.slice(7)}`;
};

function authHeaders() {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
}

// ============================================================================
// Modal "Criar/Continuar bucket" — wizard com 4 etapas orquestradas via API:
//   0) Nome + raiz                (só na criação)
//   1) DNS A (apex + wildcard)
//   2) Emissão de cert (TXTs + Continuar)
//   3) Ativo
// Quando `resumeBucket` é passado, pula direto pro estado em que o bucket
// está (útil pra retomar).
// ============================================================================
function CreateBucketDialog({ open, onClose, rootDomains, serverIp, onCreated, resumeBucket }) {
  const [step, setStep] = useState(0);
  const [slug, setSlug] = useState('');
  const [rootDomain, setRootDomain] = useState(rootDomains?.[0] || 'verificaix.com');
  const [setupInfo, setSetupInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dnsCheck, setDnsCheck] = useState(null);
  // Estado da emissão de cert
  const [bucketId, setBucketId] = useState(null);
  const [progress, setProgress] = useState(null); // último cert-progress
  const [acknowledgedTxts, setAcknowledgedTxts] = useState({}); // {name|value: true}
  const pollRef = React.useRef(null);

  // Reset ao abrir
  useEffect(() => {
    if (open) {
      if (resumeBucket) {
        setSlug(resumeBucket.slug);
        setRootDomain(resumeBucket.rootDomain);
        setBucketId(resumeBucket._id);
        setStep(2); // pula direto pra etapa de SSL
        setSetupInfo(null);
        setDnsCheck(null);
        setProgress(null);
        setAcknowledgedTxts({});
        setError('');
      } else {
        setStep(0); setSlug(''); setSetupInfo(null); setError(''); setDnsCheck(null);
        setBucketId(null); setProgress(null); setAcknowledgedTxts({});
        setRootDomain(rootDomains?.[0] || 'verificaix.com');
      }
    } else {
      // fecha → para o polling
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    }
  }, [open, rootDomains, resumeBucket]);

  const fullPreview = slug ? `${slug}.${rootDomain}` : '';

  const handleNext = async () => {
    setError('');
    if (!slug) { setError('Informe o nome do bucket.'); return; }
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/api/admin/domain-buckets/setup-info`,
        { params: { slug, rootDomain }, headers: authHeaders() }
      );
      if (res.data.success) {
        setSetupInfo(res.data);
        setStep(1);
      } else {
        setError(res.data.message || 'Erro ao preparar setup.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao preparar setup.');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckDns = async () => {
    setError(''); setLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/domain-buckets/check-dns`,
        { slug, rootDomain },
        { headers: authHeaders() }
      );
      setDnsCheck(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao verificar DNS.');
    } finally {
      setLoading(false);
    }
  };

  // Inicia / retoma a emissão do cert. Polling começa em paralelo.
  const handleStartCert = async () => {
    setError(''); setLoading(true);
    try {
      let id = bucketId;
      if (!id) {
        const res = await axios.post(
          `${API}/api/admin/domain-buckets/start-cert`,
          { slug, rootDomain },
          { headers: authHeaders() }
        );
        if (!res.data.success) {
          setError(res.data.message || 'Erro ao iniciar emissão.');
          setLoading(false); return;
        }
        id = res.data.bucketId;
        setBucketId(id);
      } else {
        const res = await axios.post(
          `${API}/api/admin/domain-buckets/${id}/resume`,
          {},
          { headers: authHeaders() }
        );
        if (!res.data.success) {
          setError(res.data.message || 'Erro ao retomar.');
          setLoading(false); return;
        }
      }
      setStep(2);
      startPolling(id);
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao iniciar.');
    } finally {
      setLoading(false);
    }
  };

  const startPolling = (id) => {
    if (pollRef.current) clearInterval(pollRef.current);
    const tick = async () => {
      try {
        const res = await axios.get(
          `${API}/api/admin/domain-buckets/${id}/cert-progress`,
          { headers: authHeaders() }
        );
        if (res.data.success) {
          setProgress(res.data);
          if (res.data.bucket.status === 'active') {
            setStep(3);
            if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
            onCreated?.();
          } else if (res.data.bucket.status === 'failed') {
            if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
          }
        }
      } catch (err) {
        // silent
      }
    };
    tick();
    pollRef.current = setInterval(tick, 2500);
  };

  // Resume polling se abrir já em step 2 (retomada)
  useEffect(() => {
    if (open && resumeBucket && step === 2 && bucketId && !pollRef.current) {
      startPolling(bucketId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, resumeBucket, step, bucketId]);

  const handleContinue = async () => {
    setError(''); setLoading(true);
    try {
      await axios.post(
        `${API}/api/admin/domain-buckets/${bucketId}/continue`,
        {},
        { headers: authHeaders() }
      );
      setAcknowledgedTxts({});
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao continuar.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelCert = async () => {
    if (!bucketId) return;
    if (!window.confirm('Cancelar a emissão? O bucket fica como "falho" e você pode retomar depois.')) return;
    try {
      await axios.post(
        `${API}/api/admin/domain-buckets/${bucketId}/cancel`,
        {},
        { headers: authHeaders() }
      );
      onCreated?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao cancelar.');
    }
  };

  const copy = (s) => navigator.clipboard.writeText(s);

  const bucketProgress = progress?.bucket;
  const challenges = bucketProgress?.pendingChallenges || [];
  const ackedAll = challenges.length > 0 && challenges.every((c) => acknowledgedTxts[`${c.name}|${c.value}`]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { background: '#0e1320', color: '#fff' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {resumeBucket ? <Play size={20} color="#A78BFA" /> : <Plus size={20} color="#A78BFA" />}
        {resumeBucket ? `Continuar bucket: ${resumeBucket.fullDomain}` : 'Criar novo bucket'}
      </DialogTitle>
      <DialogContent>
        <Stepper activeStep={step} sx={{ mb: 3, mt: 1 }}>
          <Step><StepLabel>Nome</StepLabel></Step>
          <Step><StepLabel>DNS</StepLabel></Step>
          <Step><StepLabel>SSL</StepLabel></Step>
          <Step><StepLabel>Pronto</StepLabel></Step>
        </Stepper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {step === 0 && (
          <Box>
            <Typography sx={{ mb: 2, color: '#9CA3AF' }}>
              Escolha o nome da "conta" intermediária. Os sites criados nessa conta vão ficar em <strong style={{ color: '#A78BFA' }}>{slug || '<slug>'}.{fullPreview || rootDomain}</strong>. Use um nome próprio brasileiro, sem hífens (ex.: <em>catarinarafaelatavareslopes</em>).
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="Nome do bucket"
                value={slug}
                onChange={(e) => setSlug(formatBucketSlug(e.target.value))}
                placeholder="catarinarafaelatavareslopes"
                helperText="Só letras minúsculas e números, sem espaços nem hífens."
                sx={{ flex: 2, minWidth: 280 }}
              />
              <FormControl sx={{ flex: 1, minWidth: 200 }}>
                <InputLabel>Domínio raiz</InputLabel>
                <Select value={rootDomain} label="Domínio raiz" onChange={(e) => setRootDomain(e.target.value)}>
                  {(rootDomains || []).map((d) => (<MenuItem key={d} value={d}>{d}</MenuItem>))}
                </Select>
              </FormControl>
            </Box>
            {fullPreview && (
              <Alert severity="info" sx={{ mt: 2 }} icon={<Globe size={18} />}>
                URL final dos sites: <strong>https://&#123;empresa&#125;.{fullPreview}</strong>
              </Alert>
            )}
          </Box>
        )}

        {step === 1 && setupInfo && (
          <Box>
            <Typography sx={{ mb: 2, color: '#9CA3AF' }}>
              Adicione esses <strong>2 registros A</strong> no painel DNS do <strong>{setupInfo.rootDomain}</strong> (Hostinger). O <em>verificaix.com</em> já tem wildcard global, então qualquer um dos 2 sozinho funciona — mas adicione os 2 pra garantir.
            </Typography>
            <Table size="small" sx={{ background: 'rgba(124,58,237,0.04)', borderRadius: 2, mb: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: '#A78BFA', fontWeight: 700 }}>Tipo</TableCell>
                  <TableCell sx={{ color: '#A78BFA', fontWeight: 700 }}>Nome</TableCell>
                  <TableCell sx={{ color: '#A78BFA', fontWeight: 700 }}>Valor</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {setupInfo.dnsRecords.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ color: '#fff' }}>{r.type}</TableCell>
                    <TableCell sx={{ color: '#fff', fontFamily: 'monospace' }}>{r.name}</TableCell>
                    <TableCell sx={{ color: '#fff', fontFamily: 'monospace' }}>{r.value}</TableCell>
                    <TableCell>
                      <Tooltip title="Copiar valor"><IconButton size="small" onClick={() => copy(r.value)}><Copy size={14} color="#A78BFA" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2 }}>
              <Button variant="outlined" onClick={handleCheckDns} disabled={loading} startIcon={loading ? <CircularProgress size={16} /> : <RefreshCw size={16} />}>Verificar propagação DNS</Button>
              <Typography sx={{ color: '#9CA3AF', fontSize: '0.85rem' }}>Hostinger normalmente propaga em 1-5 min.</Typography>
            </Box>

            {dnsCheck && (
              <Alert severity={dnsCheck.ready ? 'success' : 'warning'} sx={{ mb: 2 }}>
                {dnsCheck.ready
                  ? 'DNS propagado em todos os resolvers. Você pode emitir o cert SSL agora.'
                  : `Ainda não propagou em todos — apex ${dnsCheck.apex?.propagated}/${dnsCheck.apex?.total}, wildcard ${dnsCheck.wildcard?.propagated}/${dnsCheck.wildcard?.total}.`}
              </Alert>
            )}
          </Box>
        )}

        {step === 2 && (
          <Box>
            {!bucketProgress && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress size={32} />
                <Typography sx={{ color: '#9CA3AF', mt: 2 }}>Iniciando certbot...</Typography>
              </Box>
            )}

            {bucketProgress?.status === 'failed' && (
              <Alert severity="error" icon={<AlertTriangle size={18} />} sx={{ mb: 2 }}>
                A emissão falhou: <strong>{bucketProgress.lastError || 'erro desconhecido'}</strong>
                {bucketProgress.lastLogTail && (
                  <Box component="pre" sx={{ mt: 1, p: 1, background: '#000', borderRadius: 1, fontSize: '0.7rem', maxHeight: 200, overflow: 'auto', color: '#FCA5A5' }}>
                    {bucketProgress.lastLogTail}
                  </Box>
                )}
              </Alert>
            )}

            {bucketProgress?.certState === 'validating' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, background: 'rgba(59,130,246,0.08)', borderRadius: 2, mb: 2 }}>
                <CircularProgress size={20} />
                <Typography sx={{ color: '#93C5FD' }}>Certbot está validando os TXTs com a Let's Encrypt...</Typography>
              </Box>
            )}

            {bucketProgress?.certState === 'waiting_dns' && challenges.length > 0 && (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }} icon={<Hourglass size={18} />}>
                  <strong>Adicione {challenges.length === 1 ? 'esse registro TXT' : `esses ${challenges.length} registros TXT`}</strong> na Hostinger. <strong>Os 2 podem ter o mesmo nome</strong> — adicione ambos como registros separados (a Hostinger aceita).
                </Alert>
                <Table size="small" sx={{ background: 'rgba(124,58,237,0.04)', borderRadius: 2, mb: 2 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: '#A78BFA', fontWeight: 700 }}>Tipo</TableCell>
                      <TableCell sx={{ color: '#A78BFA', fontWeight: 700 }}>Nome</TableCell>
                      <TableCell sx={{ color: '#A78BFA', fontWeight: 700 }}>Valor</TableCell>
                      <TableCell sx={{ color: '#A78BFA', fontWeight: 700 }}>Adicionei</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {challenges.map((c, i) => {
                      const key = `${c.name}|${c.value}`;
                      const acked = !!acknowledgedTxts[key];
                      // O nome que vem do certbot inclui o domínio raiz; pra exibir
                      // do jeito que a Hostinger pede (relativo), tiramos o sufixo.
                      const displayName = c.name.replace(/\.verificaix\.com\.?$/, '');
                      return (
                        <TableRow key={i}>
                          <TableCell sx={{ color: '#fff' }}>TXT</TableCell>
                          <TableCell sx={{ color: '#fff', fontFamily: 'monospace' }}>
                            {displayName}
                            <Tooltip title="Copiar nome"><IconButton size="small" sx={{ ml: 0.5 }} onClick={() => copy(displayName)}><Copy size={12} color="#A78BFA" /></IconButton></Tooltip>
                          </TableCell>
                          <TableCell sx={{ color: '#fff', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                            {c.value}
                            <Tooltip title="Copiar valor"><IconButton size="small" sx={{ ml: 0.5 }} onClick={() => copy(c.value)}><Copy size={12} color="#A78BFA" /></IconButton></Tooltip>
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant={acked ? 'contained' : 'outlined'}
                              color={acked ? 'success' : 'primary'}
                              startIcon={acked ? <Check size={14} /> : null}
                              onClick={() => setAcknowledgedTxts((s) => ({ ...s, [key]: !acked }))}
                            >
                              {acked ? 'OK' : 'Marcar'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5 }}>
                  <Typography sx={{ color: '#9CA3AF', fontSize: '0.85rem' }}>
                    Aguarde uns 30s após salvar na Hostinger antes de continuar — DNS precisa propagar.
                  </Typography>
                  <Button variant="contained" color="success" onClick={handleContinue} disabled={!ackedAll || loading} startIcon={<Check size={16} />}>
                    Já adicionei, validar
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {step === 3 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Box sx={{ width: 64, height: 64, mx: 'auto', borderRadius: '50%', background: 'rgba(16,185,129,0.15)', display: 'grid', placeItems: 'center', mb: 2 }}>
              <Check size={32} color="#10B981" />
            </Box>
            <Typography variant="h6" sx={{ color: '#fff', mb: 1 }}>Bucket ativo!</Typography>
            <Typography sx={{ color: '#9CA3AF' }}>
              Cert SSL emitido com sucesso pra <strong style={{ color: '#A78BFA' }}>*.{bucketProgress?.fullDomain}</strong>.<br />
              Agora você pode criar sites dentro dele na aba "Criar site".
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {step === 2 && bucketProgress?.status === 'pending_cert' && (
          <Button onClick={handleCancelCert} sx={{ color: '#EF4444' }} startIcon={<XIcon size={14} />}>Cancelar emissão</Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} sx={{ color: '#9CA3AF' }}>{step === 3 ? 'Fechar' : 'Sair'}</Button>
        {step === 0 && (
          <Button variant="contained" onClick={handleNext} disabled={loading || !slug}>
            {loading ? <CircularProgress size={18} /> : 'Próximo'}
          </Button>
        )}
        {step === 1 && (
          <Button variant="contained" onClick={handleStartCert} disabled={!dnsCheck?.ready || loading} startIcon={loading ? <CircularProgress size={16} /> : <Play size={16} />}>
            Emitir SSL
          </Button>
        )}
        {step === 2 && bucketProgress?.status === 'failed' && (
          <Button variant="contained" onClick={handleStartCert} disabled={loading} startIcon={<RefreshCw size={16} />}>Tentar de novo</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ============================================================================
// Página principal V2.
// ============================================================================
export default function CriarVerificaixV2() {
  const navigate = useNavigate();
  const user = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();

  const [tab, setTab] = useState(0);

  // Estado dos buckets
  const [buckets, setBuckets] = useState([]);
  const [rootDomains, setRootDomains] = useState([]);
  const [serverIp, setServerIp] = useState('');
  const [bucketsLoading, setBucketsLoading] = useState(false);
  const [openCreateBucket, setOpenCreateBucket] = useState(false);
  const [resumeBucket, setResumeBucket] = useState(null);

  // Estado do form de criação de site (espelha o V1)
  const [selectedBucketId, setSelectedBucketId] = useState('');
  const [cnpjInput, setCnpjInput] = useState('');
  const [cnpjLoading, setCnpjLoading] = useState(false);
  const [cnpjData, setCnpjData] = useState(null);
  const [formData, setFormData] = useState({ dominio: '', telefone: '', email: '', metaTag: '' });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [createdUrl, setCreatedUrl] = useState('');

  const handleResumeBucket = (bucket) => {
    setResumeBucket(bucket);
    setOpenCreateBucket(true);
  };

  const fetchBuckets = useCallback(async () => {
    setBucketsLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/domain-buckets`, { headers: authHeaders() });
      if (res.data.success) {
        setBuckets(res.data.buckets || []);
        setRootDomains(res.data.rootDomains || []);
        setServerIp(res.data.serverIp || '');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setBucketsLoading(false);
    }
  }, []);

  useEffect(() => { fetchBuckets(); }, [fetchBuckets]);

  // Auto-refresh enquanto houver bucket em pending_cert (ele pode estar
  // mudando de estado em background).
  useEffect(() => {
    const hasPending = buckets.some((b) => b.status === 'pending_cert');
    if (!hasPending) return;
    const t = setInterval(fetchBuckets, 5000);
    return () => clearInterval(t);
  }, [buckets, fetchBuckets]);

  if (user.tipo !== 'admin') {
    return (
      <Fade in timeout={400}>
        <Box sx={{ p: 4 }}>
          <Alert severity="error">Esta função é restrita ao administrador.</Alert>
          <Button onClick={() => navigate('/dashboard')} sx={{ mt: 2 }} variant="contained">Voltar</Button>
        </Box>
      </Fade>
    );
  }

  const activeBuckets = buckets.filter((b) => b.status === 'active');
  const selectedBucket = activeBuckets.find((b) => b._id === selectedBucketId);

  const activeStep = !cnpjData ? 0 : createdUrl ? 2 : 1;
  const previewUrl = (selectedBucket && formData.dominio)
    ? `https://${formData.dominio}.${selectedBucket.fullDomain}`
    : '';

  const handleBuscarCNPJ = async () => {
    const cnpjLimpo = cnpjInput.replace(/\D/g, '');
    if (cnpjLimpo.length !== 14) { setError('CNPJ deve ter 14 dígitos'); return; }
    setError(''); setCnpjLoading(true);
    try {
      const res = await axios.get(`${API}/api/sites/cnpj/${cnpjLimpo}`, { headers: authHeaders(), timeout: 15000 });
      if (res.data.success) {
        const d = res.data.data;
        setCnpjData(d);
        const baseNome = d.nome_fantasia || d.razao_social || '';
        setFormData((prev) => ({
          ...prev,
          dominio: prev.dominio || formatSlug(baseNome),
          email: prev.email || (d.email || ''),
          telefone: prev.telefone || (d.telefone ? formatTelefoneInput(d.telefone) : ''),
        }));
      } else {
        setError(res.data.message || 'CNPJ não encontrado');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao buscar CNPJ.');
    } finally {
      setCnpjLoading(false);
    }
  };

  const handleGerarSite = async () => {
    setError(''); setSuccess('');
    if (!selectedBucketId) { setError('Selecione um bucket.'); return; }
    if (!formData.dominio) { setError('Slug do site é obrigatório.'); return; }
    if (!formData.telefone) { setError('Telefone é obrigatório.'); return; }
    if (!formData.email) { setError('E-mail é obrigatório.'); return; }

    setCreating(true);
    try {
      const res = await axios.post(
        `${API}/api/sites/generate-verificaix-v2`,
        {
          cnpjData,
          telefone: formData.telefone,
          email: formData.email,
          metaTag: formData.metaTag,
          dominio: formData.dominio,
          bucketId: selectedBucketId,
        },
        { headers: authHeaders(), timeout: 60000 }
      );
      if (res.data.success) {
        setCreatedUrl(res.data.url);
        setSuccess('Site gerado com sucesso!');
        fetchBuckets();
      } else {
        setError(res.data.message || 'Erro ao gerar site.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao gerar site.');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteBucket = async (id, fullDomain) => {
    if (!window.confirm(`Remover o bucket "${fullDomain}"? O cert SSL no servidor não é apagado (precisa rodar certbot delete manualmente).`)) return;
    try {
      await axios.delete(`${API}/api/admin/domain-buckets/${id}`, { headers: authHeaders() });
      fetchBuckets();
    } catch (err) {
      alert(err.response?.data?.message || 'Erro ao remover.');
    }
  };

  return (
    <Fade in timeout={400}>
      <Box sx={{ maxWidth: 1080, mx: 'auto', p: { xs: 1, sm: 2 } }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <ShieldCheck size={28} color="#A78BFA" />
            <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700 }}>Verificaix V2</Typography>
            <Chip label="Admin" size="small" sx={{ background: 'rgba(124,58,237,0.2)', color: '#A78BFA', fontWeight: 700 }} />
          </Box>
          <Typography sx={{ color: '#9CA3AF' }}>
            Geração de sites no padrão <strong style={{ color: '#A78BFA' }}>&#123;empresa&#125;.&#123;bucket&#125;.&#123;domínio&#125;</strong> (2 níveis de subdomínio). Cada bucket isola um grupo de sites por baixo de uma "conta" intermediária.
          </Typography>
        </Box>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <Tab label="Criar site" />
          <Tab label={`Buckets (${buckets.length})`} />
        </Tabs>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {tab === 0 && (
          <Box>
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
              <Step><StepLabel>Buscar CNPJ</StepLabel></Step>
              <Step><StepLabel>Gerar site</StepLabel></Step>
              <Step><StepLabel>Concluído</StepLabel></Step>
            </Stepper>

            {activeBuckets.length === 0 && (
              <Alert severity="warning" sx={{ mb: 2 }} action={
                <Button color="inherit" size="small" onClick={() => setTab(1)}>Ir para Buckets</Button>
              }>
                Nenhum bucket ativo. Crie um bucket primeiro na aba "Buckets".
              </Alert>
            )}

            <SectionCard>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 2 }}>
                <Building2 size={20} color="#A78BFA" />
                <Typography variant="h6" sx={{ color: '#fff' }}>1. Dados da empresa (CNPJ)</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <TextField
                  label="CNPJ"
                  value={cnpjInput}
                  onChange={(e) => setCnpjInput(formatCNPJ(e.target.value))}
                  placeholder="00.000.000/0000-00"
                  sx={{ flex: 1, minWidth: 240 }}
                  disabled={!!cnpjData}
                  InputProps={{ startAdornment: (<InputAdornment position="start"><Search size={18} color="#9CA3AF" /></InputAdornment>) }}
                />
                {!cnpjData ? (
                  <Button variant="contained" onClick={handleBuscarCNPJ} disabled={cnpjLoading} sx={{ minHeight: 56, px: 3 }}>
                    {cnpjLoading ? <CircularProgress size={18} /> : 'Consultar'}
                  </Button>
                ) : (
                  <Button variant="outlined" onClick={() => { setCnpjData(null); setCreatedUrl(''); setSuccess(''); }} sx={{ minHeight: 56, px: 3 }}>
                    Trocar CNPJ
                  </Button>
                )}
              </Box>
              {cnpjData && (
                <Box sx={{ mt: 2.5, p: 2, borderRadius: 2, background: 'rgba(124,58,237,0.06)', border: '1px solid rgba(124,58,237,0.2)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Check size={16} color="#10B981" />
                    <Typography sx={{ color: '#fff', fontWeight: 600 }}>{cnpjData.razao_social}</Typography>
                  </Box>
                  <Typography sx={{ color: '#9CA3AF', fontSize: '0.85rem' }}>
                    CNPJ {formatCNPJ(String(cnpjData.cnpj || ''))} • {cnpjData.municipio}-{cnpjData.uf} • {cnpjData.bairro || '—'}
                  </Typography>
                </Box>
              )}
            </SectionCard>

            {cnpjData && !createdUrl && (
              <SectionCard>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 2 }}>
                  <Rocket size={20} color="#A78BFA" />
                  <Typography variant="h6" sx={{ color: '#fff' }}>2. Gerar site</Typography>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <FormControl>
                    <InputLabel>Bucket</InputLabel>
                    <Select
                      value={selectedBucketId}
                      label="Bucket"
                      onChange={(e) => setSelectedBucketId(e.target.value)}
                    >
                      {activeBuckets.length === 0 && <MenuItem disabled value="">Nenhum bucket ativo</MenuItem>}
                      {activeBuckets.map((b) => (
                        <MenuItem key={b._id} value={b._id}>
                          {b.fullDomain} ({b.siteCount || 0} site{(b.siteCount || 0) === 1 ? '' : 's'})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    label="Slug do site (empresa)"
                    placeholder="ttlunaconstrucoesltda"
                    value={formData.dominio}
                    onChange={(e) => setFormData({ ...formData, dominio: formatSlug(e.target.value) })}
                    InputProps={{
                      startAdornment: (<InputAdornment position="start"><Globe size={16} color="#9CA3AF" /></InputAdornment>),
                      endAdornment: selectedBucket && (
                        <InputAdornment position="end">
                          <Typography sx={{ color: '#6B7280', fontSize: '0.8rem' }}>.{selectedBucket.fullDomain}</Typography>
                        </InputAdornment>
                      ),
                    }}
                    helperText={previewUrl ? `URL final: ${previewUrl}` : 'Letras minúsculas, números e hífens.'}
                  />

                  <TextField
                    label="Telefone (com DDD)"
                    placeholder="(11) 95637-2126"
                    value={formData.telefone}
                    onChange={(e) => setFormData({ ...formData, telefone: formatTelefoneInput(e.target.value) })}
                  />
                  <TextField
                    label="E-mail"
                    placeholder="contato@empresa.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                  <TextField
                    label="Meta Tag (Facebook Domain Verification)"
                    placeholder="vt7fwbhlazoilvl2m9ff8ex0mo4r2j"
                    value={formData.metaTag}
                    onChange={(e) => setFormData({ ...formData, metaTag: e.target.value.trim() })}
                    helperText="Cole apenas o content da meta tag."
                    sx={{ gridColumn: { xs: '1', sm: '1 / span 2' } }}
                  />
                </Box>

                <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button variant="contained" onClick={handleGerarSite} disabled={creating || !selectedBucketId} sx={{ minHeight: 50, px: 4, fontWeight: 700 }}>
                    {creating ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Gerar site'}
                  </Button>
                </Box>
              </SectionCard>
            )}

            {createdUrl && (
              <SectionCard>
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Box sx={{ width: 64, height: 64, mx: 'auto', borderRadius: '50%', background: 'rgba(16,185,129,0.15)', display: 'grid', placeItems: 'center', mb: 2 }}>
                    <Check size={32} color="#10B981" />
                  </Box>
                  <Typography variant="h6" sx={{ color: '#fff', mb: 1 }}>Site gerado com sucesso</Typography>
                  <Typography sx={{ color: '#9CA3AF', mb: 3 }}>Está no ar agora (com SSL).</Typography>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.2)', borderRadius: 2, px: 2, py: 1, mb: 3 }}>
                    <Globe size={16} color="#A78BFA" />
                    <Typography sx={{ color: '#fff', fontWeight: 700 }}>{createdUrl}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <Button variant="outlined" startIcon={<Copy size={16} />} onClick={() => navigator.clipboard.writeText(createdUrl)}>Copiar URL</Button>
                    <Button variant="contained" startIcon={<ExternalLink size={16} />} href={createdUrl} target="_blank" rel="noopener">Abrir site</Button>
                    <Button onClick={() => { setCnpjData(null); setCnpjInput(''); setCreatedUrl(''); setSuccess(''); setFormData({ dominio: '', telefone: '', email: '', metaTag: '' }); }} variant="text">Criar outro</Button>
                  </Box>
                </Box>
              </SectionCard>
            )}
          </Box>
        )}

        {tab === 1 && (
          <Box>
            <SectionCard>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Server size={20} color="#A78BFA" />
                    <Typography variant="h6" sx={{ color: '#fff' }}>Buckets</Typography>
                  </Box>
                  <Typography sx={{ color: '#9CA3AF', fontSize: '0.85rem' }}>
                    IP do servidor: <strong style={{ color: '#fff', fontFamily: 'monospace' }}>{serverIp || '...'}</strong>
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="outlined" startIcon={<RefreshCw size={16} />} onClick={fetchBuckets} disabled={bucketsLoading}>Atualizar</Button>
                  <Button variant="contained" startIcon={<Plus size={16} />} onClick={() => setOpenCreateBucket(true)}>Criar bucket</Button>
                </Box>
              </Box>

              {bucketsLoading && <CircularProgress size={20} />}

              {!bucketsLoading && buckets.length === 0 && (
                <Alert severity="info" icon={<AlertCircle size={18} />}>
                  Nenhum bucket cadastrado. Clique em "Criar bucket" para começar.
                </Alert>
              )}

              {buckets.length > 0 && (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: '#A78BFA' }}>Bucket</TableCell>
                      <TableCell sx={{ color: '#A78BFA' }}>Domínio raiz</TableCell>
                      <TableCell sx={{ color: '#A78BFA' }}>Status</TableCell>
                      <TableCell sx={{ color: '#A78BFA' }}>Sites</TableCell>
                      <TableCell sx={{ color: '#A78BFA' }}>Criado em</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {buckets.map((b) => {
                      const statusInfo = {
                        active:        { color: '#10B981', bg: 'rgba(16,185,129,0.15)', label: 'ativo' },
                        pending_cert:  { color: '#F59E0B', bg: 'rgba(245,158,11,0.15)', label: 'pendente SSL' },
                        failed:        { color: '#EF4444', bg: 'rgba(239,68,68,0.15)',  label: 'falhou' },
                        disabled:      { color: '#6B7280', bg: 'rgba(107,114,128,0.15)', label: 'desabilitado' },
                      }[b.status] || { color: '#6B7280', bg: 'rgba(107,114,128,0.15)', label: b.status };
                      const canResume = b.status === 'pending_cert' || b.status === 'failed';
                      return (
                        <TableRow key={b._id} hover>
                          <TableCell sx={{ color: '#fff', fontFamily: 'monospace' }}>{b.slug}</TableCell>
                          <TableCell sx={{ color: '#fff' }}>{b.rootDomain}</TableCell>
                          <TableCell>
                            <Chip size="small" label={statusInfo.label} sx={{ background: statusInfo.bg, color: statusInfo.color, fontWeight: 700 }} />
                          </TableCell>
                          <TableCell sx={{ color: '#fff' }}>{b.siteCount || 0}</TableCell>
                          <TableCell sx={{ color: '#9CA3AF', fontSize: '0.8rem' }}>{new Date(b.createdAt).toLocaleDateString('pt-BR')}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              {canResume && (
                                <Tooltip title="Continuar emissão de SSL deste bucket">
                                  <IconButton size="small" onClick={() => handleResumeBucket(b)}><Play size={14} color="#A78BFA" /></IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title={b.siteCount > 0 ? `${b.siteCount} site(s) usam este bucket — não pode remover.` : 'Remover bucket'}>
                                <span>
                                  <IconButton size="small" onClick={() => handleDeleteBucket(b._id, b.fullDomain)} disabled={b.siteCount > 0}>
                                    <Trash2 size={14} color={b.siteCount > 0 ? '#4B5563' : '#EF4444'} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </SectionCard>

            <Alert severity="info" sx={{ background: 'rgba(124,58,237,0.06)', border: '1px solid rgba(124,58,237,0.2)', color: '#9CA3AF' }}>
              Cada bucket é uma "conta" intermediária com cert SSL próprio. O fluxo de criação tem 3 etapas: (1) escolher nome, (2) adicionar 2 registros A na Hostinger, (3) rodar 1 comando no servidor pra emitir o cert wildcard. Uma vez ativo, você pode criar quantos sites quiser dentro do bucket pela aba "Criar site".
            </Alert>
          </Box>
        )}

        <CreateBucketDialog
          open={openCreateBucket}
          onClose={() => { setOpenCreateBucket(false); setResumeBucket(null); fetchBuckets(); }}
          rootDomains={rootDomains}
          serverIp={serverIp}
          onCreated={fetchBuckets}
          resumeBucket={resumeBucket}
        />
      </Box>
    </Fade>
  );
}
